import React from 'react';

export type ChartType = 'bar' | 'horizontalBar' | 'pie' | 'donut' | 'line' | 'area';

interface ChartTypeSelectorProps {
  questionType: string;
  selectedChart: ChartType;
  onChartChange: (chartType: ChartType) => void;
}

// Define which chart types are available for each question type
const CHART_OPTIONS: Record<string, ChartType[]> = {
  radio: ['horizontalBar', 'bar', 'pie', 'donut'],
  dropdown: ['horizontalBar', 'bar', 'pie', 'donut'],
  checkbox: ['horizontalBar', 'bar', 'pie'],
  yesno: ['donut', 'pie', 'bar'],
  number: ['bar', 'line', 'area'],
  rating: ['bar', 'line', 'area'],
  scale: ['bar', 'line', 'area'],
};

// Chart type icons as SVG paths
const ChartIcons: Record<ChartType, React.ReactNode> = {
  bar: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path d="M2 11h3v6H2v-6zm5-4h3v10H7V7zm5-4h3v14h-3V3zm5 6h3v8h-3V9z" />
    </svg>
  ),
  horizontalBar: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path d="M3 3h10v3H3V3zm0 5h14v3H3V8zm0 5h8v3H3v-3z" />
    </svg>
  ),
  pie: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 2v6h6a6 6 0 01-6 6V4z" />
    </svg>
  ),
  donut: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 3a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6z" clipRule="evenodd" />
    </svg>
  ),
  line: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
      <path d="M2 14l4-4 4 2 4-6 4 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  area: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path d="M2 14l4-4 4 2 4-6 4 2v8H2v-2z" opacity="0.5" />
      <path d="M2 14l4-4 4 2 4-6 4 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const ChartLabels: Record<ChartType, string> = {
  bar: 'Bar',
  horizontalBar: 'H-Bar',
  pie: 'Pie',
  donut: 'Donut',
  line: 'Line',
  area: 'Area',
};

const ChartTypeSelector: React.FC<ChartTypeSelectorProps> = ({
  questionType,
  selectedChart,
  onChartChange,
}) => {
  const availableCharts = CHART_OPTIONS[questionType] || ['horizontalBar'];

  // Don't render if only one option or text questions
  if (availableCharts.length <= 1 || ['text', 'textarea', 'email'].includes(questionType)) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      {availableCharts.map((chartType) => (
        <button
          key={chartType}
          onClick={(e) => {
            e.stopPropagation();
            onChartChange(chartType);
          }}
          title={ChartLabels[chartType]}
          className="p-1.5 rounded transition-all"
          style={{
            backgroundColor: selectedChart === chartType ? 'var(--color-accent)' : 'transparent',
            color: selectedChart === chartType ? 'white' : 'var(--color-muted)',
            border: selectedChart === chartType ? 'none' : '1px solid var(--glass-border-light)',
          }}
          onMouseEnter={(e) => {
            if (selectedChart !== chartType) {
              e.currentTarget.style.backgroundColor = 'rgba(199, 161, 74, 0.1)';
              e.currentTarget.style.color = 'var(--color-accent)';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedChart !== chartType) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-muted)';
            }
          }}
        >
          {ChartIcons[chartType]}
        </button>
      ))}
    </div>
  );
};

export default ChartTypeSelector;
